import { shallowEqual, useSelector } from 'react-redux';

import { log } from 'src/app/utils/logging';
import verifyString from 'src/app/v1/views/hooks/useDraft/mapper/utils/verifyString';
import {
  useGetSuggestedSalaryQuery,
  type WorkType,
  type SalaryType,
} from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';
import type { SuggestedSalary } from 'src/types/salaryTypes';

import { mapResponse } from './utils/mapResponse';
import { mapUISalaryTypeToHirer } from './utils/mapSalaryType';
import { mapUIWorkTypeToHirer } from './utils/mapWorkType';

export const useGetSuggestedSalaryVariables = () => {
  const {
    jobLocationCountry,
    jobLocationId,
    jobTitle,
    payType,
    workType,
    salaryCurrency,
  } = useSelector(
    (state: AppState) => ({
      jobLocationCountry: state.session.jobLocationCountry,
      jobLocationId: state.formData.jobLocationId,
      jobTitle: state.formData.jobTitle,
      payType: state.formData.payType,
      workType: state.formData.workType,
      salaryCurrency: state.formData.salaryCurrency,
    }),
    shallowEqual,
  );

  const salaryType = mapUISalaryTypeToHirer(payType);
  const hirerWorkType = mapUIWorkTypeToHirer(workType);

  const countryCode = jobLocationCountry?.toUpperCase();

  return {
    countryCode,
    currency: verifyString(salaryCurrency),
    locationId: jobLocationId?.toString(),
    roleTitle: jobTitle,
    workType: hirerWorkType as WorkType,
    salaryType: salaryType as SalaryType,
  };
};

export const useSuggestedSalary = (): {
  suggestedSalary: SuggestedSalary | null;
  error: Error | null;
  loading: boolean;
} => {
  const { countryCode, currency, locationId, workType, roleTitle, salaryType } =
    useGetSuggestedSalaryVariables();

  const { data, error, loading } = useGetSuggestedSalaryQuery({
    variables: {
      countryCode,
      currency,
      locationId,
      roleTitle,
      salaryType,
      workType,
    },
    skip: !countryCode || !locationId || !roleTitle || !workType || !salaryType,
  });

  if (error) {
    log.error('useSuggestedSalary | useGetSuggestedSalaryQuery', {
      error,
      countryCode,
      currency,
      locationId,
      roleTitle,
      salaryType,
      workType,
    });
  }

  return {
    suggestedSalary: mapResponse(data?.suggestedSalary),
    error: error ? new Error(error.message) : null,
    loading,
  };
};
